import validator from 'email-validator';
import emoji from 'node-emoji';
import React, { useCallback, useEffect } from 'react';
import { Alert, StyleSheet, View } from 'react-native';
import { AppView, Button, CollapsableContainer, rem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS, SettingsItem, SettingsItemContent, SettingsItemRightContent, SettingsItemTitle, Spacer, withAnimatedNavigationHeader, } from 'design-system';
import { IconEmail, IconPhone, IconTrash } from 'design-system/icons';
import IconPlus from 'design-system/icons/IconPlus';
import { deleteMonitoredAcc, getMonitoredAccounts, saveMonitoredEmail, saveMonitoredPhone, } from 'actions';
import { setDeviceSettingsKey } from 'actions/deviceSettings';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { logQuest } from 'features/quests/actions';
import { muteAll, useFetchOld } from 'hooks';
import Animated from 'react-native-reanimated';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import StatusBar from 'design-system/StatusBar';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { focus } from 'hooks/useAutoFocusInput';
import { formatPhoneNumber } from 'utils';
import TouchableBounce from 'utils/packages/TouchableBounce';
import { goBackToTopWithStops } from 'utils/navigationv6';
import { updateSetUpEmmaStep } from 'features/setUpEmma/actions';
import { EmmaSetUpStepId } from 'features/feed/components/completeSetup/entities';
import { isWeb } from '../../constants';
const AddNewItem = (props) => (<SettingsItem icon={<IconPlus colorKey="brand"/>} title="Add new" iconSize={44} {...props}/>);
const DeletableItem = ({ title, onDelete, ...props }) => (<SettingsItem iconSize={44} {...props}>
    <SettingsItemContent>
      <SettingsItemTitle>{title}</SettingsItemTitle>
    </SettingsItemContent>
    <SettingsItemRightContent>
      <TouchableBounce onPress={onDelete} style={styles.deleteButton}>
        <IconTrash colorKey="secondary"/>
      </TouchableBounce>
    </SettingsItemRightContent>
  </SettingsItem>);
const DataProtectionSettingsScreen = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const { isCreateFlow, onResetBreaches } = route.params;
    const dispatch = useAppDispatch();
    const [, , , fetch] = useFetchOld(muteAll);
    useEffect(() => {
        dispatch(getMonitoredAccounts());
    }, []);
    const phones = useAppSelector((store) => store.dataProtection.monitoredAccPhones);
    const emails = useAppSelector((store) => store.dataProtection.monitoredAccEmails);
    const resetBreaches = React.useCallback(() => {
        if (onResetBreaches) {
            onResetBreaches();
        }
        else {
            dispatch(getMonitoredAccounts());
        }
    }, [onResetBreaches]);
    const onAddNewEmail = useCallback(() => {
        navigation.navigateDeprecated('TextFieldPage', {
            title: 'Enter your email',
            placeholder: 'Email',
            buttonTitle: 'Continue',
            textFieldProps: {
                autoCapitalize: 'none',
                keyboardType: 'email-address',
            },
            onPressNext: (value, _, inputRef) => {
                if (!value?.length || !validator.validate(value.trim())) {
                    Alert.alert(emoji.emojify('Hold on :dizzy:'), 'You must enter an email.', [{ text: 'Okay', onPress: () => focus(inputRef) }]);
                }
                else {
                    fetch(saveMonitoredEmail(value)).then(() => {
                        resetBreaches();
                        navigation.pop();
                    });
                }
            },
        });
    }, [resetBreaches]);
    const onAddNewPhone = useCallback(() => {
        navigation.navigateDeprecated('PhoneFieldPage', {
            title: 'Enter your mobile number',
            placeholder: 'Phone number',
            buttonTitle: 'Continue',
            onPressNext: (value) => {
                fetch(saveMonitoredPhone(value)).then(() => {
                    resetBreaches();
                    navigation.pop();
                });
            },
        });
    }, [resetBreaches]);
    const onDeleteMonitoredAcc = useCallback(async (id) => {
        await fetch(deleteMonitoredAcc(id));
        resetBreaches();
    }, [resetBreaches]);
    const handleContinue = useCallback(() => {
        if (!phones.length && !emails.length) {
            Alert.alert('Hold on 💫', 'Please add at least an email or phone number so that we can start the monitoring for you.');
            return;
        }
        dispatch(logQuest('SETUP_FRAUD_DETECT'));
        dispatch(setDeviceSettingsKey('didViewFraudDetectionIntro', true));
        const continueEmmaSetUpScreen = navigation
            .getState()
            .routes.find((route) => route.name === 'ContinueEmmaSetUp');
        const setUpEmmaStepsScreen = navigation
            .getState()
            .routes.find((route) => route.name === 'SetUpEmmaSteps');
        if (continueEmmaSetUpScreen || setUpEmmaStepsScreen) {
            dispatch(updateSetUpEmmaStep(EmmaSetUpStepId.PROTECT_YOUR_IDENTITY, true));
        }
        navigation.navigateDeprecated('Success', {
            title: 'Fraud detection is now active',
            subTitle: 'We’ve started monitoring your personal details for any signs of a data breach',
            onContinue: () => {
                goBackToTopWithStops(navigation, [
                    'Report',
                    'AccountSettings',
                    'SetUpEmmaSteps',
                    'ContinueEmmaSetUp',
                ]);
                if (!continueEmmaSetUpScreen && !setUpEmmaStepsScreen) {
                    navigation.navigateDeprecated('DataProtectionScreen');
                }
                return true;
            },
            buttonText: 'Done',
        });
    }, [emails.length, phones.length]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', isCreateFlow ? HEIGHT_PLUS_PADDING : SCREEN_BOTTOM_PADDING, paddingHorizontalStyle, styles.containerStyle);
    return (<AppView withFrame={false}>
          <StatusBar barStyle="dark-content" animated/>
          <Animated.ScrollView onScroll={scrollHandler} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} scrollEventThrottle={16} contentContainerStyle={contentContainerStyle}>
            {!!isCreateFlow && (<ScreenInfoHeader title="What details do you want to monitor?" subTitle="You will see if they have been leaked and receive alerts if they will be in the future"/>)}
            <View style={styles.container}>
              <CollapsableContainer title="Emails" isCollapsedByDefault={false}>
                <AddNewItem onPress={onAddNewEmail}/>
                {emails.map(({ id, account }) => (<DeletableItem key={id} icon={<IconEmail gradientKey="darkPink"/>} title={account} onDelete={() => onDeleteMonitoredAcc(id)}/>))}
              </CollapsableContainer>
              <Spacer.H16 />
              <CollapsableContainer title="Phone numbers" isCollapsedByDefault={false}>
                <AddNewItem onPress={onAddNewPhone}/>
                {phones.map(({ id, account }) => (<DeletableItem key={id} icon={<IconPhone gradientKey="darkPurple"/>} title={formatPhoneNumber(account)} onDelete={() => onDeleteMonitoredAcc(id)}/>))}
              </CollapsableContainer>
            </View>
          </Animated.ScrollView>
          {!!isCreateFlow && (<Button brand floating title="Continue" onPress={handleContinue}/>)}
        </AppView>);
}, ({ colors, route }) => ({
    title: 'Personal details',
    hideTitleOnScroll: route.params.isCreateFlow,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    containerStyle: {
        paddingTop: rem(16),
    },
    container: {
        paddingHorizontal: rem(16),
    },
    deleteButton: {
        marginLeft: rem(16),
    },
});
export default DataProtectionSettingsScreen;
